import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../../utils/datetime';
import { WORKSPACE_ROOT } from '../../navigation';

const roundMoney = (value) => Math.round((Number(value) || 0) * 100) / 100;

const cashOutMaxFor = (payout) =>
  roundMoney(payout?.cashOutMax ?? (payout?.cleared || 0) + (payout?.failed || 0));

const CashOutPanel = ({ payout, currency }) => {
  const navigate = useNavigate();
  const max = cashOutMaxFor(payout);
  const ready = Boolean(payout?.destination?.numberMasked);
  const feePercent = payout?.platformFeePercent || 0;
  const [amount, setAmount] = useState(max > 0 ? String(max) : '');

  useEffect(() => {
    setAmount(max > 0 ? String(max) : '');
  }, [max]);

  const parsed = roundMoney(amount);
  const canContinue = ready && max > 0 && parsed > 0 && parsed <= max;

  const submit = (event) => {
    event.preventDefault();
    if (!canContinue) return;
    navigate(
      `${WORKSPACE_ROOT}/sales/transactions/cash-out?amount=${encodeURIComponent(parsed)}`,
      { state: { amount: parsed } }
    );
  };

  return (
    <form
      onSubmit={submit}
      className="w-full min-w-[16rem] bg-ink-900 px-5 py-4 text-sand-50 sm:max-w-sm"
    >
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-[11px] uppercase tracking-[0.14em] text-saffron-400">Cash out</p>
        <p className="text-[11px] uppercase tracking-[0.12em] text-sand-50/55">
          Max {formatCurrency(max, currency)} after fees
        </p>
      </div>

      <div className="mt-3 flex gap-2">
        <label className="sr-only" htmlFor="cash-out-amount">
          Amount to cash out
        </label>
        <input
          id="cash-out-amount"
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          max={max || undefined}
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
          disabled={!ready || max <= 0}
          className="min-w-0 flex-1 bg-ink-800 px-3 py-2.5 font-display text-2xl font-light tabular-nums text-sand-50 outline-none ring-1 ring-sand-50/15 focus:ring-saffron-400 disabled:opacity-50"
        />
        <button
          type="button"
          onClick={() => setAmount(String(max))}
          disabled={!ready || max <= 0}
          className="flex-none px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-saffron-400 ring-1 ring-sand-50/15 hover:bg-ink-800 disabled:opacity-50"
        >
          Max
        </button>
      </div>

      <button
        type="submit"
        disabled={!canContinue}
        className="mt-3 w-full bg-saffron-500 px-4 py-3.5 text-sm font-semibold uppercase tracking-[0.14em] text-ink-900 hover:bg-saffron-400 disabled:opacity-40"
      >
        Cash out
      </button>

      <p className="mt-2 text-[11px] leading-snug text-sand-50/55">
        {!ready
          ? 'Connect a payout account in Manage → Payments.'
          : feePercent > 0
            ? `To ${payout.destination.bank || 'account'} ${payout.destination.numberMasked}. Spirara’s ${feePercent}% stays in the platform wallet.`
            : `To ${payout.destination.bank || 'account'} ${payout.destination.numberMasked}`}
      </p>
    </form>
  );
};

export default CashOutPanel;
